import { useEffect, useRef, useState } from "react";
import AdminModalNew from "./AdminModalNew";

// The single confirmation dialog for the admin dashboard. Its look is the one the
// vehicle/driver assignment popup established: a centred question, a muted line of
// consequence underneath, an optional block of detail the admin should read before
// committing, and a right-aligned Cancel / Confirm pair.
//
// Every admin "are you sure?" goes through here so the answer to "what does a
// confirmation look like?" lives in one file. Anything a caller needs to show *inside*
// the dialog goes in as `children`.

// Confirm-button tone. `danger` is for the irreversible (delete, reject, cancel a
// booking); `gold` marks a money movement the brand palette owns (charging a quotation);
// `primary` is everything else.
const CONFIRM_TONES = {
    primary: "tw:!bg-[#3CAEA3] tw:!text-white",
    danger: "tw:!bg-[#EA4335] tw:!text-white",
    gold: "tw:!bg-[#D2AF61] tw:!text-white",
};

// How long a partially-armed confirm button waits for the next click before falling
// back to zero. Long enough to read the dialog between clicks, short enough that a
// stray click on a dialog left open in another tab never counts towards a later one.
const CONFIRM_CLICK_WINDOW_MS = 3000;

// Progress pips for a multi-click confirm: filled for the clicks already registered.
const ClickProgress = ({ total, done }) => (
    <div className="tw:!flex tw:!justify-center tw:!gap-2 tw:!mb-3" aria-hidden="true">
        {Array.from({ length: total }, (_, index) => (
            <span
                key={index}
                className={`tw:!h-2.5 tw:!w-2.5 tw:!rounded-full tw:!transition-colors ${index < done ? "tw:!bg-[#3CAEA3]" : "tw:!bg-[#E0E0E0]"
                    }`}
            />
        ))}
    </div>
);

const ConfirmationModal = ({
    show,
    title,
    description,
    children,
    error = "",
    variant = "primary",
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    // Number of clicks the confirm button demands before it fires. 1 is a plain
    // dialog; raise it for a change that is site-wide, silent once applied, or
    // otherwise not something to walk into with a single reflexive click.
    confirmClicks = 1,
    // Shown on the confirm button while `isBusy` — say what is happening ("Deleting…"),
    // never just "Loading".
    busyLabel,
    isBusy = false,
    size = "modal-md",
    onCancel,
    onConfirm,
}) => {
    // Esc closes, matching every other dismissible surface — but not mid-request, where
    // closing would strand the caller's pending action behind a hidden dialog.
    useEffect(() => {
        if (!show) return;

        const onKeyDown = (event) => {
            if (event.key === "Escape" && !isBusy) onCancel?.();
        };

        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [show, isBusy, onCancel]);

    // Clicks banked so far towards a multi-click confirm.
    const requiredClicks = Math.max(1, confirmClicks);
    const [clicks, setClicks] = useState(0);
    const clickTimer = useRef(null);

    const forgetClicks = () => {
        clearTimeout(clickTimer.current);
        setClicks(0);
    };

    // A dialog that closes and reopens starts from zero, so clicks never carry over
    // from the action the admin backed out of to the next one.
    useEffect(() => {
        if (!show) forgetClicks();
    }, [show]);

    useEffect(() => () => clearTimeout(clickTimer.current), []);

    if (!show) return null;

    const confirmTone = CONFIRM_TONES[variant] || CONFIRM_TONES.primary;
    const requestClose = () => {
        if (!isBusy) {
            forgetClicks();
            onCancel?.();
        }
    };

    const remainingClicks = requiredClicks - clicks;

    const handleConfirm = () => {
        if (requiredClicks === 1) {
            onConfirm?.();
            return;
        }

        clearTimeout(clickTimer.current);

        if (remainingClicks <= 1) {
            setClicks(0);
            onConfirm?.();
            return;
        }

        setClicks(clicks + 1);
        clickTimer.current = setTimeout(() => setClicks(0), CONFIRM_CLICK_WINDOW_MS);
    };

    // The button says how many clicks are left so the first click never reads as a
    // dead button; the count is dropped while busy, where the label is the status.
    const confirmText = isBusy
        ? busyLabel || confirmLabel
        : requiredClicks > 1 && remainingClicks > 1
            ? `${confirmLabel} \u2014 ${remainingClicks} more clicks`
            : confirmLabel;

    return (
        <AdminModalNew show={show} size={size} centered onClose={requestClose}>
            {title && (
                <h5 className="tw:!text-center tw:!text-[20px] tw:!font-[600] tw:!text-[#4E4E4E] tw:!mb-2">
                    {title}
                </h5>
            )}

            {description && (
                <p className="tw:!text-center tw:!text-[14px] tw:!text-[#8D8D8D] tw:!mb-6">
                    {description}
                </p>
            )}

            {children ? <div className="tw:mb-8">{children}</div> : null}

            {error ? (
                <p className="tw:!mb-4 tw:!text-center tw:!text-[14px] tw:!text-[#EA4335]">{error}</p>
            ) : null}

            {requiredClicks > 1 && !isBusy ? (
                <>
                    <p className="tw:!mb-2 tw:!text-center tw:!text-[13px] tw:!text-[#8D8D8D]">
                        This one needs a deliberate confirmation: click{" "}
                        <span className="tw:!font-[600] tw:!text-[#4E4E4E]">{confirmLabel}</span>{" "}
                        {requiredClicks} times in a row.
                    </p>
                    <ClickProgress total={requiredClicks} done={clicks} />
                </>
            ) : null}

            <div className="tw:!flex tw:!justify-center tw:!gap-2">
                <button
                    type="button"
                    disabled={isBusy}
                    onClick={requestClose}
                    className="tw:!py-3 tw:!px-8 tw:!border-[1.5px] tw:!border-[#D9D9D9] tw:!rounded-md tw:!text-[#282828] tw:!font-[400] tw:!text-[15px] tw:!bg-white tw:!cursor-pointer tw:disabled:cursor-not-allowed tw:disabled:opacity-50"
                >
                    {cancelLabel}
                </button>
                <button
                    type="button"
                    disabled={isBusy}
                    onClick={handleConfirm}
                    className={`tw:!py-3 tw:!px-12 tw:!rounded-md tw:!font-[400] tw:!text-[15px] tw:!cursor-pointer tw:disabled:cursor-not-allowed tw:disabled:opacity-70 ${confirmTone}`}
                >
                    {confirmText}
                </button>
            </div>
        </AdminModalNew>
    );
};

export default ConfirmationModal;
